import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import Tooltip from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap.css';
import { CSVLink } from 'react-csv';
import useApiContext from 'hooks/useApiContext';
import useModalContext from 'hooks/useModalContext';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { format, isAfter } from 'date-fns';
import useRepo from 'hooks/useRepo';
import { T, A } from 'TOMA';
import s from './EventList.module.scss';

import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Switch } from 'components/Switch';
import { CustomLink } from 'components/CustomLink';
import { InputStand } from 'components/InputStand';
import Extraction from 'components/Extraction/Extraction';
import { EventRepository } from 'services/Event/Event.repository';
import routes from '../../router/routes';
import { eRoles } from '../../interfaces/Roles';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Typography,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckIcon from '@mui/icons-material/Check';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';

const columnHelper = createColumnHelper<any>();

const SIDEBAR_WIDTH = 230;
// Height of the app's sticky top header (#app-header Toolbar minHeight), so the
// fixed sidebar starts below it instead of being hidden underneath.
const HEADER_HEIGHT = 70;

interface SidebarItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  matchFn: (p: string) => boolean;
}

// Builds "{slug}-{year}" without duplicating the year if the slug already ends with it
const withYear = (
  slug?: string | null,
  year?: string | number | null
): string => {
  if (!slug) return '';
  if (!year) return slug;
  return slug.endsWith(`-${year}`) ? slug : `${slug}-${year}`;
};

function EventList(): JSX.Element {
  const { pathname } = useLocation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const {
    codificationRepository,
    eventRepository,
    userRepository,
    categoryRepository,
  } = useApiContext();
  const [filterCollectif] = useState<any>();
  const [filterName, setFilterName] = useState<any>();
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [reload, setReload] = useState<number>(0);
  const { setModalContent, setModalVisible } = useModalContext();
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [copyEventId, setCopyEventId] = useState<string>('');
  const [logosLoadingId, setLogosLoadingId] = useState<number | null>(null);

  // Download all exhibitor HD logos of an event as one ZIP file
  const downloadExhibitorLogos = async (
    eventId: number,
    slug?: string
  ): Promise<void> => {
    setLogosLoadingId(eventId);
    try {
      toast(t('event:download_exhibitors_hd_logos_started'), { type: 'info' });
      const blob = await eventRepository.downloadExhibitorLogosZip(eventId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${slug ?? `event-${eventId}`}-hd-logos.zip`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast(err.message, { type: 'error' });
    } finally {
      setLogosLoadingId(null);
    }
  };
  const navigate = useNavigate();
  const { t } = useTranslation();

  const copyToClipboardFallback = (text: string): void => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }
    document.body.removeChild(textarea);
  };

  const user = userRepository.getUser();
  const isModerator = user?.role === eRoles.MODERATOR;
  const isAdmin = user?.role === eRoles.ADMIN;

  const { response = [], loading } = useRepo(
    async () => await eventRepository.getEvents(),
    [reload]
  );

  const { response: categories } = useRepo(
    async () => await categoryRepository.parentCategory('universe'),
    [pathname]
  );

  const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
      padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
    },
  }));

  const { response: codifCollectif } = useRepo(
    async () => await codificationRepository.get('collectif')
  );

  const handleCreateEvent = async (eventId: string): Promise<void> => {
    const newEvent = await eventRepository.createCopyEvent(eventId);
    if (newEvent) {
      setReload((prev) => prev + 1);
      setOpenPopup(false);
    }
  };

  const handleClose = (): void => {
    setOpenPopup(false);
  };

  const columns = useMemo(
    () => [
      ...(!isModerator
        ? [
            columnHelper.accessor('remove', {
              header: '',
              enableSorting: false,
              cell: (info) => {
                const val = info.getValue();

                return (
                  <button
                    key={val.eventId}
                    type="button"
                    className={s.ExhibitorList__remove}
                    onClick={() => {
                      setModalContent(
                        <div>
                          <A.Text fontSize="22" fw="bold" mb="20">
                            Êtes vous sur de vouloir supprimer cet évènement ?
                          </A.Text>
                          <T.Flex gap={2} valign="center" mb="-15">
                            <A.Button
                              bg="alert"
                              onClick={() => {
                                eventRepository.remove(val.eventId).then(() => {
                                  setModalVisible(false);
                                  info.row.toggleSelected();
                                });
                              }}
                            >
                              Confirmer
                            </A.Button>
                            <A.Button
                              appearance="link"
                              color="grey-80"
                              mb="15"
                              textDecoration="underline"
                              onClick={() => setModalVisible(false)}
                            >
                              Annuler
                            </A.Button>
                          </T.Flex>
                        </div>
                      );
                    }}
                  >
                    {val.removed ? (
                      <A.Icon icon="trash3-fill" color="alert" fontSize="22" />
                    ) : (
                      <A.Icon icon="trash2" color="alert" fontSize="22" />
                    )}
                  </button>
                );
              },
            }),
          ]
        : []),
      columnHelper.accessor('visibility', {
        header: 'Online',
        enableSorting: false,
        cell: ({ getValue }) => {
          const { value, eventId } = getValue();
          return (
            <Switch
              key={eventId}
              checked={value}
              disable={user?.role !== eRoles.ADMIN}
              onChange={async (e) =>
                await eventRepository.mergeEvent('visibility', e, eventId)
              }
            />
          );
        },
      }),
      columnHelper.accessor('name', {
        header: t('event:name_of_the_event'),
        enableSorting: false,
        cell: ({ getValue }) => {
          const { value, eventId, year } = getValue();

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CustomLink
                to={routes.admEventList.path + `/${eventId}`}
                color="info"
              >
                {value} {year}
              </CustomLink>

              <button
                type="button"
                onClick={() => {
                  setCopyEventId(eventId);
                  setOpenPopup(true);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  marginLeft: '6px',
                }}
              >
                <A.Icon icon="clipboard" fontSize="16" color="grey-60" />
              </button>
            </div>
          );
        },
      }),
      columnHelper.accessor('date', {
        header: t('event:date'),
        enableSorting: false,
        cell: (info) => {
          const val = info.getValue();
          return (
            <div className={s.ExhibitorList__status} data-key={val}>
              {format(new Date(val), 'dd/MM/yyyy')}
            </div>
          );
        },
      }),
      columnHelper.accessor('country', {
        header: t('event:country'),
        enableSorting: false,
        cell: (info) => {
          const val = info.getValue();
          return (
            <div className={s.ExhibitorList__status} data-key={val}>
              {val}
            </div>
          );
        },
      }),
      columnHelper.accessor('creator', {
        header: t('event:creator'),
        enableSorting: false,
        enableHiding: true,
        cell: ({ getValue }) => {
          const { creator } = getValue();
          return (
            <div className={s.ExhibitorList__status}>
              {creator ? creator.firstname : 'admin'}
            </div>
          );
        },
      }),
      columnHelper.accessor('url', {
        header: t('event:preview'),
        enableSorting: false,
        cell: ({ getValue }) => {
          const { slug: baseSlug, year } = getValue();
          const slug = withYear(baseSlug, year);

          return (
            <>
              <CustomLink
                to={routes.home2.path + `preview/events/${slug}/exhibitors`}
                color="info"
              >
                {t('event:exhibitor_list')}
              </CustomLink>
              <CustomLink
                to={routes.home2.path + `preview/events/${slug}/products`}
                color="info"
              >
                {t('event:product_list')}
              </CustomLink>
              <CustomLink
                to={routes.home2.path + `published/${slug}`}
                color="info"
              >
                {t('event:offer_page')}
              </CustomLink>
            </>
          );
        },
      }),
      columnHelper.accessor('url', {
        header: '',
        enableSorting: false,
        cell: ({ getValue }) => {
          const { value, eventId } = getValue();

          return (
            <>
              <CustomLink
                to={routes.home2.path + `events/${eventId}/invite`}
                color="info"
              >
                Invite
              </CustomLink>
            </>
          );
        },
      }),
    ],
    []
  );

  const data = useMemo(
    () =>
      response
        .filter((e) => {
          const regex = new RegExp(filterName, 'gi');
          const nameMatch = filterName
            ? regex.test(e.name ?? '') || regex.test(e.name ?? '')
            : true;
          const categoryMatch = filterCategory
            ? (e as any).category_id?.toString() === filterCategory
            : true;
          return nameMatch && categoryMatch;
        })
        .map((e) => {
          const collectif = '';
          return {
            remove: {
              eventId: e.id,
              role:
                e.creator && (e.creator as { role?: string }).role
                  ? (e.creator as { role: string }).role
                  : undefined,
            },
            visibility: {
              value: !!e.visibility,
              eventId: e.id,
            },
            date: e.date,
            country: e.country,
            name: {
              value: e.name,
              eventId: e.id,
              year: e.year,
            },
            url: {
              eventId: e.id,
              slug: e.slug,
              year: e.year,
            },
            form: {
              eventId: e.id,
            },
            creator: {
              creator: e.creator,
            },
            exhibitors: {
              eventId: e.id,
              exhibitors_count: e.exhibitors_count,
            },
          };
        }),
    [response, codifCollectif, filterCollectif, filterName, filterCategory]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    manualPagination: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const isDrawerOpen = pathname !== routes.admEventList.path;

  const closeDrawer = (): void => {
    setReload((prev) => prev + 1);
    navigate(routes.admEventList.path);
  };

  const drawerTitle = pathname.includes('new-event')
    ? t('event:new_event')
    : pathname.includes('invite')
    ? 'Invitation'
    : t('event:events');

  const sidebarItems = useMemo<SidebarItem[]>(() => {
    if (isAdmin) {
      return [
        {
          label: t('header:users'),
          icon: <PeopleIcon fontSize="small" />,
          path: routes.profile.path,
          matchFn: (p) => p.includes('users'),
        },
        {
          label: t('header:events'),
          icon: <CalendarMonthIcon fontSize="small" />,
          path: routes.admEventList.path,
          matchFn: (p) => p.includes('events'),
        },
        {
          label: t('header:exhibitor'),
          icon: <GroupsIcon fontSize="small" />,
          path: routes.home.path,
          matchFn: (p) =>
            p.includes('exhibitor') && !p.includes('exhibitor-list'),
        },
        {
          label: t('header:company_settings'),
          icon: <BusinessIcon fontSize="small" />,
          path: routes.company.path,
          matchFn: (p) => p.includes('company'),
        },
      ];
    }
    if (isModerator) {
      return [
        {
          label: t('header:events'),
          icon: <CalendarMonthIcon fontSize="small" />,
          path: routes.admEventList.path,
          matchFn: (p) => p.includes('events'),
        },
        {
          label: t('header:exhibitor'),
          icon: <GroupsIcon fontSize="small" />,
          path: routes.home.path,
          matchFn: (p) =>
            p.includes('exhibitor') && !p.includes('exhibitor-list'),
        },
      ];
    }
    return [];
  }, [isAdmin, isModerator, t]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F9FAFB' }}>
      {/* ── Left sidebar ── */}
      {sidebarItems.length > 0 && (
        <Box
          component="nav"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            bgcolor: '#fff',
            borderRight: '1px solid #eee',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: HEADER_HEIGHT,
            left: 0,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            overflowY: 'hidden',
          }}
        >
          {/* Sidebar header */}
          <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  bgcolor: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
              </Box>
              <Typography
                sx={{ fontSize: '13px', fontWeight: 600, color: '#111' }}
              >
                {t('header:events')}
              </Typography>
            </Box>
          </Box>

          {/* Nav items */}
          <List disablePadding sx={{ py: 1.5, flex: 1 }}>
            {sidebarItems.map((item) => {
              const isActive = item.matchFn(pathname);
              return (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    component={CustomLink as any}
                    to={item.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      px: 2,
                      py: 0.875,
                      mx: 1,
                      mb: 0.25,
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#f5f5f5' : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive ? '#eeeeee' : '#f9f9f9',
                      },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 30, color: isActive ? '#111' : '#bbb' }}
                    >
                      {isActive ? (
                        <CheckIcon sx={{ fontSize: 17 }} />
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? '#111' : '#666',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {/* ── Main content ── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          marginLeft: sidebarItems.length > 0 ? `${SIDEBAR_WIDTH}px` : 0,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
          {/* Page header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Box>
              <Box sx={{ fontSize: '22px', fontWeight: 700, color: '#111' }}>
                {t('event:list_of_events')}
              </Box>
              {!loading && (
                <Box sx={{ fontSize: '13px', color: '#888', mt: 0.5 }}>
                  {data.length} événement{data.length !== 1 ? 's' : ''}
                </Box>
              )}
            </Box>
            {!isModerator && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(routes.newEvent.path)}
                sx={{
                  backgroundColor: '#000',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '13px',
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 2,
                  py: 1,
                  '&:hover': { backgroundColor: '#222' },
                }}
              >
                {t('event:new_event')}
              </Button>
            )}
          </Box>

          {/* Filters bar */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              placeholder={t('event:events')}
              onChange={(e) => setFilterName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '8px', fontSize: '13px', bgcolor: '#fff' },
              }}
              sx={{ minWidth: 220 }}
            />
            <Box
              component="select"
              value={filterCategory}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setFilterCategory(e.target.value)
              }
              sx={{
                padding: '8px 12px',
                fontSize: '13px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                backgroundColor: 'white',
                minWidth: '180px',
                outline: 'none',
                cursor: 'pointer',
                color: '#333',
                '&:focus': { borderColor: '#000' },
              }}
            >
              <option value="">
                {t('event:all_categories') || 'All categories'}
              </option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.topicCode}
                </option>
              ))}
            </Box>
          </Box>

          {/* Event cards */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={32} sx={{ color: '#000' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {data.map((row, index) => {
                const nameVal = row.name;
                const visibilityVal = row.visibility;
                const removeVal = row.remove;
                const urlVal = row.url;
                const creatorVal = row.creator;
                const isFirst = index === 0;

                return (
                  <Box
                    component="a"
                    href={routes.admEventList.path + `/${nameVal?.eventId}`}
                    key={row.name?.eventId ?? index}
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      p: { xs: 2, md: 3 },
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                      border: '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: 'inherit',
                      flexWrap: 'wrap',
                      transition: 'box-shadow 0.15s',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    {/* Visibility toggle */}
                    <Box sx={{ flexShrink: 0 }}>
                      <Switch
                        key={visibilityVal?.eventId}
                        checked={visibilityVal?.value}
                        disable={user?.role !== eRoles.ADMIN}
                        onChange={async (e) =>
                          await eventRepository.mergeEvent(
                            'visibility',
                            e,
                            visibilityVal?.eventId as number
                          )
                        }
                      />
                    </Box>

                    {/* Event name + meta */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <CustomLink
                        to={routes.admEventList.path + `/${nameVal?.eventId}`}
                        color="info"
                      >
                        <Box
                          sx={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#111',
                            mb: 0.5,
                            lineHeight: 1.3,
                          }}
                        >
                          {nameVal?.value} {nameVal?.year}
                        </Box>
                      </CustomLink>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                          fontSize: '12px',
                          color: '#888',
                        }}
                      >
                        {row.date && (
                          <span>
                            📅 {format(new Date(row.date), 'dd/MM/yyyy')}
                          </span>
                        )}
                        {row.country && <span>📍 {row.country}</span>}
                        {creatorVal?.creator && (
                          <span>
                            👤{' '}
                            {(creatorVal.creator as any).firstname ?? 'admin'}
                          </span>
                        )}
                      </Box>
                    </Box>

                    {/* Preview links */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexShrink: 0,
                        flexWrap: 'wrap',
                      }}
                    >
                      {urlVal?.slug && (
                        <>
                          <CustomLink
                            to={
                              routes.home2.path +
                              `preview/events/${withYear(
                                urlVal.slug,
                                urlVal.year
                              )}/exhibitors`
                            }
                            color="info"
                          >
                            <Chip
                              label={t('event:exhibitor_list')}
                              size="small"
                              clickable
                              sx={{
                                fontSize: '11px',
                                bgcolor: 'f5f5f5',
                                color: '#555',
                                border: 'none',
                              }}
                            />
                          </CustomLink>
                          <CustomLink
                            to={
                              routes.home2.path +
                              `preview/events/${withYear(
                                urlVal.slug,
                                urlVal.year
                              )}/products`
                            }
                            color="info"
                          >
                            <Chip
                              label={t('event:product_list')}
                              size="small"
                              clickable
                              sx={{
                                fontSize: '11px',
                                bgcolor: '#f5f5f5',
                                color: '#555',
                                border: 'none',
                              }}
                            />
                          </CustomLink>
                        </>
                      )}
                      <CustomLink
                        to={
                          routes.home2.path +
                          `published/${withYear(urlVal?.slug, urlVal?.year)}`
                        }
                        color="info"
                      >
                        <Chip
                          label="Invite"
                          size="small"
                          clickable
                          sx={{
                            fontSize: '11px',
                            bgcolor: '#f5f5f5',
                            color: '#555',
                            border: 'none',
                          }}
                        />
                      </CustomLink>
                      <Chip
                        label={t('event:download_exhibitors_hd_logos')}
                        size="small"
                        clickable
                        disabled={logosLoadingId === nameVal?.eventId}
                        onClick={(e) => {
                          // the whole card is a link: keep the click from navigating
                          e.preventDefault();
                          e.stopPropagation();
                          void downloadExhibitorLogos(
                            nameVal?.eventId as number,
                            urlVal?.slug
                          );
                        }}
                        sx={{
                          fontSize: '11px',
                          bgcolor: '#f5f5f5',
                          color: '#555',
                          border: 'none',
                        }}
                      />
                    </Box>

                    {/* Copy + Delete */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCopyEventId(String(nameVal?.eventId ?? ''));
                          setOpenPopup(true);
                        }}
                        sx={{
                          color: '#bbb',
                          '&:hover': { color: '#555' },
                        }}
                        title="Copier l'événement"
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      {!isModerator && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setModalContent(
                              <div>
                                <A.Text fontSize="22" fw="bold" mb="20">
                                  Êtes vous sur de vouloir supprimer cet
                                  évènement ?
                                </A.Text>
                                <T.Flex gap={2} valign="center" mb="-15">
                                  <A.Button
                                    bg="alert"
                                    onClick={() => {
                                      eventRepository
                                        .remove(String(removeVal.eventId))
                                        .then(() => {
                                          setModalVisible(false);
                                          setReload((e) => e + 1);
                                        });
                                    }}
                                  >
                                    Confirmer
                                  </A.Button>
                                  <A.Button
                                    appearance="link"
                                    color="grey-80"
                                    mb="15"
                                    textDecoration="underline"
                                    onClick={() => setModalVisible(false)}
                                  >
                                    Annuler
                                  </A.Button>
                                </T.Flex>
                              </div>
                            );
                          }}
                          sx={{
                            color: '#ccc',
                            '&:hover': { color: '#dc2626' },
                          }}
                          title="Supprimer"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {!loading && data.length === 0 && (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    color: '#aaa',
                    fontSize: '14px',
                  }}
                >
                  Aucun événement trouvé
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Right-side Drawer for new event / invite ── */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 600 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid #eee',
            position: 'sticky',
            top: 0,
            bgcolor: '#fff',
            zIndex: 1,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
            {drawerTitle}
          </Typography>
          <IconButton size="small" onClick={closeDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <Outlet />
        </Box>
      </Drawer>

      {/* ── Copy event popup ── */}
      {openPopup && (
        <BootstrapDialog
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={openPopup}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{ textAlign: 'center' }}
            id="customized-dialog-title"
          >
            {t('event:confirmation')}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            Souhaitez-vous copier pour créer un nouvel événement ?
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ width: '100%' }}
            >
              {t('common:close')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              onClick={() => {
                handleCreateEvent(copyEventId);
              }}
              sx={{
                backgroundColor: '#28AB6E',
                color: 'white',
                width: '100%',
                '&:hover': { backgroundColor: '#1e8d5f' },
              }}
            >
              {t('signup:form.validate')}
            </Button>
          </DialogActions>
        </BootstrapDialog>
      )}
    </Box>
  );
}

export default EventList;
